import type { ComponentType, SVGProps } from 'react';
import PeasantSVG from '../characters/icons/peasant.svg?react';
import AssassinSVG from '../characters/icons/assassin.svg?react';
import KnightSVG from '../characters/icons/knight.svg?react';
import CavalrySVG from '../characters/icons/cavalry.svg?react';
import MageSVG from '../characters/icons/mage.svg?react';
import HeroSVG from '../characters/icons/hero.svg?react';
import KingSVG from '../characters/icons/king.svg?react';
import {
  avatarFill,
  avatarStroke,
  getProfileAvatarDef,
  type PieceKey,
  type ProfileAvatarDef,
} from '../profile-avatars/registry';
import { getProfileFrameDef } from '../profile-frames/registry';

export type ProfileAvatarProps = {
  avatarId?: string | null;
  frameId?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  ring?: boolean;
};

const sizeClass = {
  sm: 'h-11 w-11',
  md: 'h-16 w-16',
  lg: 'h-28 w-28 sm:h-36 sm:w-36',
  xl: 'h-36 w-36 sm:h-44 sm:w-44 md:h-52 md:w-52',
} as const;

const iconPad = {
  sm: 'p-2',
  md: 'p-3',
  lg: 'p-6 sm:p-7',
  xl: 'p-7 sm:p-8',
} as const;

const framePad = {
  sm: 'p-0.5',
  md: 'p-1',
  lg: 'p-1.5',
  xl: 'p-2',
} as const;

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** Same Heroicons / FA marks used by classic board pieces. */
const PIECE_ICON: Record<PieceKey, IconComponent> = {
  king: KingSVG,
  knight: KnightSVG,
  hero: HeroSVG,
  mage: MageSVG,
  cavalry: CavalrySVG,
  assassin: AssassinSVG,
  peasant: PeasantSVG,
};

export default function ProfileAvatar({
  avatarId,
  frameId,
  size = 'md',
  className = '',
  ring = true,
}: ProfileAvatarProps) {
  const def: ProfileAvatarDef = getProfileAvatarDef(avatarId);
  const frame = getProfileFrameDef(frameId);
  const fill = avatarFill(def);
  const stroke = avatarStroke(def);
  const frameAccent = frame?.accent;
  const showRing = ring || !!frame;
  const Icon = PIECE_ICON[def.pieceKey];

  const inner = (
    <span
      className={[
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-bk-bg',
        showRing && !frame
          ? 'border-2 border-bk-gold-dim shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_3px_rgba(0,0,0,0.45)]'
          : frame
            ? 'border-2 border-black/40'
            : '',
        sizeClass[size],
        iconPad[size],
        !frame ? className : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        background: `radial-gradient(circle at 35% 30%, color-mix(in srgb, ${fill} 42%, #121826), #0a0e17 72%)`,
        color: fill,
      }}
      title={def.label}
      aria-label={def.label}
    >
      <Icon
        className="h-full w-full drop-shadow-[0_1px_1px_rgba(0,0,0,0.65)]"
        style={{ color: fill, fill }}
        aria-hidden
      />
      <span
        className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset"
        style={{
          boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${stroke} 25%, transparent)`,
        }}
      />
    </span>
  );

  if (!frame) return inner;

  return (
    <span
      className={[
        'inline-flex shrink-0 rounded-full',
        framePad[size],
        className,
      ].join(' ')}
      style={{
        background: `linear-gradient(145deg, ${frameAccent}, color-mix(in srgb, ${frameAccent} 35%, #0a0e17) 55%, ${frameAccent})`,
        boxShadow: `0 0 0 1px color-mix(in srgb, ${frameAccent} 50%, transparent), 0 0 16px color-mix(in srgb, ${frameAccent} 35%, transparent)`,
      }}
      title={frame.label}
    >
      {inner}
    </span>
  );
}
